import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCrashTrackIncidentInfoQuery } from "../../api/network/adminApiServices";
import { WeatherData, WeatherDataDetails } from "../../api/types/CrashTrack";
import svgIcons from "../../assets/svg/svgIcons";
import { useLoggedInUserData } from "../../utils/user";
import { crashtrack_tab_slugs } from "./crash-track-admin-details";
import { CrashTrackTabParams } from "./type";
import { APP_CONFIG } from "../../constants/constants";

const COLUMNS_TO_SHOW_WHEN_PRINTING = 5;

// Helper Function to convert Celsius to Fahrenheit
const celsiusToFahrenheit = (celsius: number): number => (celsius * 9) / 5 + 32;
const ScreenCrashTrackTabWeatherInfo = ({
  assetCrashIdAsset,
  assetCrashIdTimestamp,
  onLoadComplete,
  printMode = false,
}: CrashTrackTabParams) => {
  const tab_slug = crashtrack_tab_slugs[6]; // "weather_information";
  const { t: tCrashTrackDetails } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage`,
  });
  const { t } = useTranslation("translation", {
    keyPrefix: `admins.crashtrack.detailsPage.tab_${tab_slug}`,
  });
  const hasCalledLoadComplete = useRef(false);

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [temperatureUnit, setTemperatureUnit] = useState<"C" | "F">("C");
  const {
    data: dataCrashTrackIncidentInfo,
    isFetching: isFetchingCrashTrackIncidentInfo,
    isError: isErrorCrashTrackIncidentInfo,
  } = useCrashTrackIncidentInfoQuery(
    { assetId: assetCrashIdAsset, timestamp: assetCrashIdTimestamp },
    { skip: !assetCrashIdAsset || !assetCrashIdTimestamp }
  );
  useEffect(() => {
    if (
      printMode &&
      !isFetchingCrashTrackIncidentInfo &&
      !hasCalledLoadComplete.current
    ) {
      onLoadComplete?.();
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "===== CRASH TRACK TAB LOADED",
          tab_slug,
          new Date().getTime()
        );
      hasCalledLoadComplete.current = true;
    }
  }, [printMode, isFetchingCrashTrackIncidentInfo, onLoadComplete]);

  const weatherData = useMemo(() => {
    if (isFetchingCrashTrackIncidentInfo || isErrorCrashTrackIncidentInfo)
      return null;

    const weatherDataRaw = dataCrashTrackIncidentInfo?.weather_data;
    if (!weatherDataRaw) return null;

    if (typeof weatherDataRaw === "string") {
      try {
        return JSON.parse(weatherDataRaw) as WeatherData;
      } catch {
        return null;
      }
    } else {
      return weatherDataRaw as WeatherData;
    }
  }, [
    dataCrashTrackIncidentInfo,
    isFetchingCrashTrackIncidentInfo,
    isErrorCrashTrackIncidentInfo,
  ]);

  const tableData = useMemo(() => {
    if (!weatherData?.data) return [];

    const data = weatherData.data;
    const weatherKeys = Object.keys(data) as Array<keyof WeatherDataDetails>;
    const temperatureKeys: (keyof WeatherDataDetails)[] = [
      "temperature",
      "temperatureChange24Hour",
      "temperatureDewPoint",
      "temperatureFeelsLike",
      "temperatureHeatIndex",
      "temperatureMax24Hour",
      "temperatureMaxSince7Am",
      "temperatureMin24Hour",
      "temperatureWindChill",
    ];
    return weatherKeys.map((key) => {
      const isTempKey = temperatureKeys.includes(key);
      let processedValues: (number | string)[];
      processedValues = data[key] || [];

      if (isTempKey && temperatureUnit === "F") {
        processedValues = processedValues.map((value) =>
          typeof value === "number" ? celsiusToFahrenheit(value) : value
        );
      }

      const formattedLabel = key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (c) => c.toUpperCase());

      return {
        label: isTempKey
          ? `${formattedLabel} (°${temperatureUnit})`
          : formattedLabel,
        values: processedValues,
      };
    });
  }, [weatherData, temperatureUnit]);

  if (isFetchingCrashTrackIncidentInfo)
    return (
      <div className="flex-grow flex items-center justify-center min-h-64">
        <img src={svgIcons.LoadingAnimation} alt="" className="size-12" />
      </div>
    );

  if (isErrorCrashTrackIncidentInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 text-center">
        <p className="font-bold text-xl text-gray-400 px-8">
          {tCrashTrackDetails("tab_data_fetch_error")}
        </p>
      </div>
    );
  }
  const timeColumns = tableData[0]?.values.length || 0;

  return (
    <>
      <div className="Xprintable-landscape-page flex-grow flex flex-col gap-4 p-4 overflow-hidden">
        <div className="no-print flex justify-between items-center mb-2">
          <h2 className="font-semibold text-lg">{t("heading")}</h2>
          {/* Toggle  */}
          <label
            htmlFor={"temperatureUnit"}
            className={`flex gap-2 justify-between items-center rounded-lg shadow-sm cursor-pointer w-fit p-1`}
          >
            <span className="font-normal text-lg px-1">C</span>
            <input
              id={"temperatureUnit"}
              type={"checkbox"}
              checked={temperatureUnit === "F"}
              onChange={(e) => setTemperatureUnit(e.target.checked ? "F" : "C")}
              className="sr-only peer focus-visible:outline-4"
            />
            <div className="relative w-12 h-7 bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:start-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
            <span className="font-normal text-lg px-1">F</span>
          </label>
        </div>

        <div
          className={`relative flex-grow${printMode ? "" : " overflow-x-auto"}`}
        >
          {/* actual table (replica below) */}
          <table
            className={`${printMode ? "" : "absolute "}w-full bg-white${printMode ? " table-fixed" : ""}`}
            style={
              printMode ? {} : { minWidth: `${timeColumns * 150 + 320}px` }
            }
          >
            <tbody>
              <tr
                className="visible-on-print" // `visible-on-print` defined in index.scss
              >
                <th
                  className="pb-4 text-left"
                  colSpan={
                    1 +
                    (printMode
                      ? COLUMNS_TO_SHOW_WHEN_PRINTING
                      : tableData.length)
                  }
                >
                  <h2 className="font-semibold text-lg">{t("heading")}</h2>
                </th>
              </tr>
              {tableData.map((item, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={rowIndex > 0 ? "border-t border-t-gray-200" : ""}
                >
                  <td
                    className={`px-6 py-4 font-semibold bg-white${printMode ? "" : " min-w-80 max-w-80 sticky left-0"}`}
                  >
                    {item.label}
                  </td>
                  {/* Data columns */}
                  {item.values
                    .slice(
                      0,
                      !printMode && !!timeColumns
                        ? timeColumns
                        : COLUMNS_TO_SHOW_WHEN_PRINTING
                    ) // item.values.length or timeColumns
                    .map((value: any, colIndex: number) => (
                      <td
                        key={colIndex}
                        className={`px-6 py-4 text-sm${printMode ? "" : " min-w-[150px] whitespace-nowrap"}`}
                      >
                        <span className="block break-all">
                          {value !== null && value !== undefined
                            ? typeof value === "number" &&
                              !Number.isInteger(value)
                              ? parseFloat(value.toFixed(2)).toString()
                              : value.toString()
                            : "--"}
                        </span>
                      </td>
                    ))}
                </tr>
              ))}
            </tbody>
          </table>
          {/* invisible replica table - added to make the parent div take up the necessary vertical space */}
          {!printMode && (
            <table
              className={`invisible w-full bg-white${printMode ? " table-fixed" : ""}`}
              // style={printMode ? {} : { minWidth: `${timeColumns * 150 + 320}px` }}
            >
              <tbody style={{ pageBreakInside: "avoid" }}>
                {tableData.map((item, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={rowIndex > 0 ? "border-t border-t-gray-200" : ""}
                  >
                    <td
                      className={`px-6 py-4 font-semibold bg-white${printMode ? "" : " min-w-80 max-w-80 sticky left-0"}`}
                    >
                      {item.label}
                    </td>
                    {/* Data columns */}
                    {/* truncated */}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="printable-page-break" />
    </>
  );
};
export default ScreenCrashTrackTabWeatherInfo;
